import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/quantum-shop";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6, width = 1600, height = 900) => ({
  src: `${dir}/${file}.webp`,
  src900: `${dir}/${file}-900.webp`,
  alt,
  width,
  height,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string, height = 1238) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 760,
  height,
  span: 2 as const,
});

export const quantumShop: CaseStudy = {
  slug: "quantum-shop",
  title: "Quantum Shop",
  cardTitle: "Разработка интернет-магазина профессиональной косметики Quantum Shop",
  subtitle: "Интернет-магазин профессиональной косметики",
  industry: { label: "Интернет-магазины", slug: "ecommerce" },
  eyebrow: "Интернет-магазин · Профессиональная косметика",
  h1: "Разработка интернет-магазина профессиональной косметики Quantum Shop",
  lead:
    "Собрали интернет-магазин официального дистрибьютора профессиональной косметики: каталог на несколько тысяч товаров и сотни брендов, подбор по типу ухода, карточка товара, корзина и личный кабинет.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить интернет-магазин" },

  seo: {
    primaryKeyword: "разработка интернет-магазина профессиональной косметики",
    secondaryKeywords: [
      "интернет-магазин косметики разработка",
      "создание магазина косметики",
      "e-commerce для косметического бренда",
      "продвижение интернет-магазина косметики",
    ],
    semanticEntities: [
      "интернет-магазин косметики",
      "каталог брендов",
      "категории ухода",
      "карточка товара",
      "корзина",
      "личный кабинет",
      "подарочный сертификат",
      "акции и подборки",
    ],
    excludedKeywords: [
      "купить косметику",
      "крем для лица",
      "шампунь купить",
      "профессиональная косметика Москва",
    ],
    title: "Интернет-магазин Quantum Shop — кейс HAY.agency",
    description:
      "Кейс HAY.agency: интернет-магазин Quantum Shop с большим каталогом брендов, категориями ухода, акциями, личным кабинетом и продвижением.",
    h1: "Разработка интернет-магазина профессиональной косметики Quantum Shop",
    slug: "quantum-shop",
    canonicalUrl: `${SITE_URL}/cases/quantum-shop`,
    ogTitle: "Интернет-магазин профессиональной косметики Quantum Shop",
    ogDescription:
      "Большой каталог брендов, категории ухода, карточка товара, корзина и личный кабинет для официального дистрибьютора.",
    ogImage: `${SITE_URL}${dir}/quantum-shop-og.webp`,
    robotsIndex: true,
    datePublished: "2024-05-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка и развитие интернет-магазинов",
      to: routes.ecom,
      note: "Каталог, карточка товара и сценарии покупки",
    },
    relatedIndustry: {
      label: "SEO-продвижение интернет-магазинов",
      to: routes.seo,
      note: "Видимость каталога, категорий и брендов",
    },
    relatedCases: [
      {
        label: "Сайт и продвижение сети клиник Quantum Clinic",
        to: "/cases/quantum-clinic",
        note: "Корпоративный проект той же группы компаний",
      },
      {
        label: "Разработка интернет-магазина цветов «Цветочный маркет»",
        to: "/cases/cvmarket",
        note: "Каталог, карточка товара и мобильное оформление заказа",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен интернет-магазин с большим каталогом?",
    text: "Спроектируем каталог, фильтры и карточку товара, соберём магазин и возьмём его на развитие.",
    button: "Обсудить интернет-магазин",
  },

  client: {
    name: "Quantum Shop",
    url: "https://quantum-shop.ru/",
    city: "Москва",
    industry: "Профессиональная косметика, e-commerce",
  },
  year: "С 2024 года",
  services: [
    "Дизайн интернет-магазина",
    "Разработка каталога и фильтров",
    "Карточка товара и корзина",
    "Личный кабинет",
    "SEO-продвижение",
    "Сопровождение проекта",
  ],
  platform: "1С-Битрикс",
  tags: [
    { label: "Интернет-магазин", to: routes.ecom },
    { label: "Разработка", to: routes.ecom },
    { label: "SEO", to: routes.seo },
    { label: "Поддержка", to: routes.ecom },
  ],
  facts: [
    { label: "Клиент", value: "Quantum Shop" },
    { label: "Отрасль", value: "Профессиональная косметика" },
    { label: "Тип проекта", value: "Интернет-магазин официального дистрибьютора" },
    { label: "Платформа", value: "1С-Битрикс" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Интернет-магазин официального дистрибьютора профессиональной косметики",
      "Каталог с разделами «Волосы», «Лицо», «Тело», «Руки», «Гигиена и уход», «Макияж», «Для дома»",
      "Фильтры и подбор товара внутри крупных категорий",
      "Карточка товара с фото, характеристиками, ценой и наличием",
      "Корзина, избранное, личный кабинет и подарочный сертификат",
      "Разделы акций, брендов и блога",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "Магазину профессиональной косметики нужен был e-commerce, который выдерживает более пяти тысяч товаров и двести брендов и при этом не превращает выбор в перебор карточек. Покупатель приходит либо за конкретным брендом, либо за решением задачи ухода — оба пути должны работать одинаково быстро.",
        "Вторая задача — связать магазин с брендом клиник Quantum Clinic: посетитель клиники должен легко переходить к покупке средств, которые ему рекомендовали.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: товар и цена на первом плане",
    paragraphs: [
      "Интерфейс намеренно светлый и нейтральный: цвет работает только в акцентах — акциях, кнопке «В корзину» и статусах наличия. Крупная товарная сетка позволяет сравнивать позиции без входа в карточку.",
      "Категории на витрине показаны фотографиями продуктов — это ускоряет вход в нужный раздел ухода даже для нового покупателя.",
    ],
    bullets: [
      "Крупная товарная сетка и одинаковые карточки",
      "Акцентный оранжевый — только для действий и акций",
      "Витрина категорий вместо длинного мегаменю",
    ],
  },

  identity: {
    fonts: [
      { name: "Гротеск Bold", role: "Заголовки и цены", sample: "Каталог" },
      { name: "Гротеск Regular", role: "Интерфейс и описания", sample: "Доставка и оплата" },
    ],
    colors: [
      { hex: "#F26B21", name: "Акцент действия" },
      { hex: "#F4F0EA", name: "Фон витрины" },
      { hex: "#1A1A1A", name: "Текст" },
    ],
  },

  cover: img(
    "quantum-shop-case-cover",
    "Главная страница интернет-магазина профессиональной косметики Quantum Shop",
    6,
  ),

  gallery: [
    img(
      "quantum-shop-catalog-filters",
      "Каталог Quantum Shop: категории ухода, фильтры и товарная сетка",
      6,
    ),
  ],

  responsive: {
    text: [
      "Мобильная версия проектировалась как основной сценарий покупки: поиск по каталогу, категория, карточка товара и корзина.",
      "Цена, наличие и кнопка покупки видны без прокрутки, а фильтры открываются отдельным экраном, чтобы не мешать просмотру товаров.",
    ],
    images: [
      mobile("quantum-shop-mobile-home", "Мобильная главная интернет-магазина Quantum Shop"),
      mobile("quantum-shop-mobile-catalog", "Мобильный каталог категорий ухода Quantum Shop"),
    ],
  },

  columnBlocks: [
    {
      id: "catalog",
      title: "Большой каталог и подбор товара",
      paragraphs: [
        "Каталог разделён по зонам ухода и дополнительным сценариям — подарки, товары для дома, наборы, сертификаты. Внутри крупных категорий работают подкатегории и фильтры, чтобы покупатель сокращал выборку до десятка позиций.",
      ],
      columns: [
        { title: "Уход", items: ["Волосы", "Лицо", "Тело", "Руки и ноги"] },
        { title: "Дополнительно", items: ["Макияж", "Парфюмерия", "Витамины и добавки"] },
        { title: "Сценарии покупки", items: ["Наборы", "Для дома", "Подарочный сертификат"] },
      ],
      images: [
        img(
          "quantum-shop-catalog-filters",
          "Категории каталога и фильтры интернет-магазина косметики",
          6,
        ),
      ],
      caption: "Категории на витрине показаны фотографиями — это ускоряет вход в нужный раздел.",
    },
    {
      id: "purchase",
      title: "Карточка товара и покупка",
      paragraphs: [
        "Карточка показывает фотографии, состав и характеристики, цену, наличие и связанные товары. Дальше покупателя ведут корзина, избранное и оформление заказа с личным кабинетом и историей покупок.",
      ],
      bullets: [
        "Фото, характеристики и описание средства",
        "Цена, наличие и быстрый переход в корзину",
        "Избранное и повторный заказ из личного кабинета",
      ],
    },
    {
      id: "brands",
      title: "Бренды, акции и контент",
      paragraphs: [
        "Отдельные точки входа — страницы брендов, раздел акций и подарочные сертификаты. Блог и переход на сайт клиник Quantum Clinic связывают магазин с экспертизой сети.",
      ],
      columns: [
        { title: "Бренды", items: ["Страницы брендов", "Подборки дистрибьютора"] },
        { title: "Мотивация", items: ["Акции и скидки", "Подарочный сертификат"] },
        { title: "Контент", items: ["Блог о уходе", "Связка с Quantum Clinic"] },
      ],
    },
    {
      id: "promotion",
      title: "Развитие и продвижение",
      paragraphs: [
        "Магазин остаётся на нашем сопровождении: развиваем каталог, работаем с поисковой видимостью категорий и брендов, поддерживаем техническую часть. Показатели раскроем после согласования с клиентом.",
      ],
      bullets: [
        "SEO категорий, брендов и карточек товара",
        "Развитие каталога и подборок",
        "Техническая поддержка магазина",
      ],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка интернет-магазина", to: routes.ecom },
      { label: "SEO-продвижение", to: routes.seo },
      { label: "Поддержка и развитие", to: routes.ecom },
    ],
  },
};
